/*
  BagStatsCard — "Your bag today"
  ------------------------------------------------------------------------
  Aggregates every GPS-tracked shot the user has ever logged (via the
  in-round "+ Log shot" button or the auto-detect state machine) into a
  per-club summary — count, avg carry, longest, last-used timestamp.

  Backend: GET /api/auth/me/bag-stats
    → returns { clubs: [{club, count, avg, longest, last_used_at}], ... }

  Rendered on the Profile page above the Payment section so mates can see
  their real bag come to life after a few rounds. Empty state nudges them
  to try shot logging.

  Deliberately keeps the visualisation simple — a horizontal bar chart
  keyed off the longest club so relative distances are obvious at a
  glance. A full-blown dispersion / p50-p75 chart lives in Stats.jsx.
*/
import { useEffect, useState } from "react";
import { Barbell, Sparkle, ArrowRight } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";

const CLUB_LABELS = {
  driver: "Driver", "3w": "3-wood", "5w": "5-wood", "7w": "7-wood", hybrid: "Hybrid",
  "2i": "2-iron", "3i": "3-iron", "4i": "4-iron", "5i": "5-iron", "6i": "6-iron",
  "7i": "7-iron", "8i": "8-iron", "9i": "9-iron",
  pw: "PW", gw: "GW", sw: "SW", lw: "LW", putter: "Putter",
};

export default function BagStatsCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/auth/me/bag-stats");
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Could not load bag stats");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // While loading we still occupy vertical space so the profile page
  // doesn't jump when the fetch resolves.
  if (loading) {
    return (
      <div className="surface-card-flat p-5 mb-4 opacity-60" data-testid="bag-stats-loading">
        <div className="flex items-center gap-2 mb-2">
          <Barbell size={18} weight="fill" className="text-[var(--accent-terracotta)]" />
          <h3 className="font-heading font-bold text-[var(--primary)]">Your bag today</h3>
        </div>
        <p className="text-xs text-[var(--text-secondary)]">Loading your shot stats…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="surface-card-flat p-5 mb-4" data-testid="bag-stats-error">
        <div className="flex items-center gap-2 mb-2">
          <Barbell size={18} weight="fill" className="text-[var(--accent-terracotta)]" />
          <h3 className="font-heading font-bold text-[var(--primary)]">Your bag today</h3>
        </div>
        <p className="text-xs text-[var(--status-bad)]">{error}</p>
      </div>
    );
  }

  const clubs = data?.clubs || [];
  const totalShots = data?.total_shots || 0;
  const rounds = data?.rounds_with_shots || 0;

  // No shots logged yet → empty state that hooks people into trying it.
  if (clubs.length === 0) {
    return (
      <div className="surface-card-flat p-5 mb-4" data-testid="bag-stats-empty">
        <div className="flex items-center gap-2 mb-2">
          <Barbell size={18} weight="fill" className="text-[var(--accent-terracotta)]" />
          <h3 className="font-heading font-bold text-[var(--primary)]">Your bag today</h3>
        </div>
        <p className="text-xs text-[var(--text-secondary)] leading-snug mb-3">
          Log shots during your round (tap <strong>+ Log shot</strong> or flip the <strong>Auto</strong> pill on) and we&apos;ll build your real bag with actual carry distances — no more guessing which club to hit from 150.
        </p>
        <Link
          to="/app"
          data-testid="bag-stats-start-round"
          className="inline-flex items-center gap-1 text-xs font-bold text-[var(--primary)] underline"
        >
          Start a round <ArrowRight size={11} weight="bold" />
        </Link>
      </div>
    );
  }

  // Scale bars off the LONGEST club so the driver anchors the chart.
  const maxAvg = Math.max(...clubs.map((c) => c.avg));

  return (
    <div className="surface-card-flat p-5 mb-4" data-testid="bag-stats-card">
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Barbell size={20} weight="fill" className="text-[var(--accent-terracotta)]" />
          <div>
            <h3 className="font-heading font-bold text-[var(--primary)] leading-tight">Your bag today</h3>
            <p className="text-[10px] uppercase tracking-widest text-[var(--text-secondary)]">
              {totalShots} shots · {rounds} rounds
            </p>
          </div>
        </div>
        <span className="text-[10px] font-bold text-[var(--accent-terracotta)] flex items-center gap-1">
          <Sparkle size={11} weight="fill" />
          LIVE
        </span>
      </div>

      <div className="space-y-1.5">
        {clubs.map((c) => {
          const pct = Math.round((c.avg / maxAvg) * 100);
          return (
            <div
              key={c.club}
              className="grid items-center gap-2 text-xs"
              style={{ gridTemplateColumns: "58px 1fr 58px" }}
              data-testid={`bag-stats-row-${c.club}`}
            >
              <span className="font-bold text-[var(--primary)] truncate">
                {CLUB_LABELS[c.club] || c.club}
              </span>
              <div className="h-4 rounded-full bg-[var(--secondary)] relative overflow-hidden">
                <div
                  className="absolute inset-y-0 left-0 rounded-full"
                  style={{
                    width: `${pct}%`,
                    background: "linear-gradient(90deg, var(--primary) 0%, var(--accent-terracotta) 100%)",
                    transition: "width .35s ease",
                  }}
                />
                <span className="absolute inset-0 flex items-center justify-center text-[10px] font-mono-num font-black text-white mix-blend-difference">
                  {Math.round(c.avg)}y avg
                </span>
              </div>
              <span className="text-right text-[10px] text-[var(--text-secondary)] font-mono-num">
                {c.count} · {Math.round(c.longest)}y
              </span>
            </div>
          );
        })}
      </div>

      <p className="text-[10px] text-[var(--text-secondary)] mt-3 leading-snug">
        Averages update after every shot. Once you have 5+ shots on a club we&apos;ll feed the average straight into Smart Caddie so it stops guessing yardages.
      </p>
    </div>
  );
}
